import { Component, OnInit } from '@angular/core';
import { VVerificationDto, VVerificationServiceServiceProxy, VVerificationQuestionAnswerServiceServiceProxy, VVerificationQuestionWithAnswersDto, VVerificationQuestionAnswerDto, VAnswerDto, VVerificationCreateDto, VVerificationStatusEnum, VVerificationQuestionNoteServiceServiceProxy, VVerificationQuestionNoteDto, VVerificationQuestionWithAnswersGroupDto, CountyDto, CountyServiceServiceProxy } from '@shared/service-proxies/service-proxies';
import { AlertController, LoadingController, ModalController, NavController, Platform } from '@ionic/angular';
import { AppStorageService } from '@shared/storage/app-storage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AppSessionService } from '@shared/session/app-session.service';
import { VerificationQuestionTypeEnum } from './model/verification-question-type-enum';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Map, tileLayer, marker } from 'leaflet';
import { Title } from '@angular/platform-browser';
import domtoimage from 'dom-to-image';
import html2canvas from 'html2canvas';
import { AppConsts } from '@shared/AppConsts';
import { VerificationQuestionNotePage } from './verification-question-note/verification-question-note.page';
import { PermissionCheckerService } from '@shared/auth/permission-checker.service';

@Component({
  selector: 'app-verification',
  templateUrl: './verification.page.html',
  styleUrls: ['./verification.page.scss'],
})
export class VerificationPage implements OnInit {

  public readonly QuestionTypes = VerificationQuestionTypeEnum;
  public readonly StatusTypes = VVerificationStatusEnum;

  id: number;
  formGroup: FormGroup;
  verification: VVerificationDto;
  verificationQuestionsWithAnswersGroups: VVerificationQuestionWithAnswersGroupDto[] = [];
  map: Map;
  isLoaded: boolean = false;
  encToken: string;
  counties: CountyDto[] = [];

  private baseUrl: string = AppConsts.remoteServiceBaseUrl;
  constructor(
    private formBuilder: FormBuilder,
    private alertController: AlertController,
    private loadingController: LoadingController,
    storageService: AppStorageService,
    private route: ActivatedRoute,
    private router: Router,
    private verificationService: VVerificationServiceServiceProxy,
    private verificationQuestionAnswerService: VVerificationQuestionAnswerServiceServiceProxy,
    private verificationQuestionNoteService: VVerificationQuestionNoteServiceServiceProxy,
    private sessionService: AppSessionService,
    private titleService: Title,
    public platform: Platform,
    appStorageService:AppStorageService,
    private modalController: ModalController,
    private navCtrl: NavController,
    private countyService: CountyServiceServiceProxy,
    private permissionChecker: PermissionCheckerService
  ) {
    storageService.IsLoadedSubject.subscribe(x => { 
      if (!this.isLoaded && x) {
        this.route.queryParams.subscribe(params => {
          if (params && params.verificationId) {
            this.id = +params.verificationId;
            if (this.id > 0) { // id is required
              this.getVerification();
            }
          }
          appStorageService.EncryptedTokenSubject.subscribe(x=> this.encToken = x);
          this.getCounties();
          this.isLoaded = true;
        });
      }
    });
    this.titleService.setTitle('OACI - Assessment');
    this.setupForm();
  }

  ngOnInit() {
  }
  ionViewDidEnter(){
    this.loadMap();
  }

  setupForm() {
    if (this.formGroup) {
      this.formGroup.reset();
    }
    this.formGroup = this.formBuilder.group({
      farmName: ['', Validators.required],
      countyId: ['', Validators.required],
      longitude: ['', Validators.required],
      latitude: ['', Validators.required],
    });
  }

  getCounties() {
    this.countyService.getAll().subscribe(counties => {
      this.counties = counties;
    });
  }
  
  loadMap(){
    this.map = new Map("mapId").setView([17.3850,78.4867], 13);
  }

  getLocation(){
    this.map.locate({setView:true}).on("locationfound", (e: any)=> {
      this.formGroup.patchValue({
        latitude: e.latitude,
        longitude: e.longitude
      });
      this.setInProgress();
    });
  }

  async getVerification() {
    
    const loading = await this.loadingController.create({
      message: 'Fetching assessment...',
    });
    await loading.present();

    this.verificationService.get(this.id).subscribe(async verification => {
      this.verification = verification;
      this.formGroup.patchValue({
        farmName: verification.farmName,
        countyId: verification.countyId,
        longitude: verification.longitude,
        latitude: verification.latitude,
      });
      this.verificationQuestionAnswerService.getAllByVerificationId(this.id).subscribe(async verificationQuestionsWithAnswersGroups => {
        this.verificationQuestionsWithAnswersGroups = verificationQuestionsWithAnswersGroups;
        if (this.verification.statusId == VVerificationStatusEnum._2 && !this.permissionChecker.isGranted("Role.Admin")) {
          if (this.canComplete()) {
            this.formGroup.disable();
          }
        }
        await loading.dismiss();
      });
    });
  }

  isSingleSelect(questionTypeId) {
    return questionTypeId == this.QuestionTypes.SingleSelect || questionTypeId == this.QuestionTypes.SingleSelectOrFreeText;
  }

  isMultiSelect(questionTypeId) {
    return questionTypeId == this.QuestionTypes.MultiSelect || questionTypeId == this.QuestionTypes.MultiSelectAndOrFreeText;
  }

  isFreeText(questionTypeId) {
    return questionTypeId == this.QuestionTypes.FreeText;
  }

  isAnswerWithFreeText(answer: VAnswerDto) {
    return answer.requiresFreeText;
  }

  isAnswerSelected(vqa: VVerificationQuestionAnswerDto, answer: VAnswerDto) {
    return vqa ? vqa.vAnswerId == answer.id : false;
  }

  isAnswerSelectedMultiple(vqas: VVerificationQuestionAnswerDto[], answer: VAnswerDto) {
    return vqas && answer ? vqas.find(x => x.vAnswerId == answer.id) != null : false;
  }

  selectedMultipleAnswerAlternate(vqas: VVerificationQuestionAnswerDto[], answer: VAnswerDto) {
    return vqas && answer ? vqas.find(x => x.vAnswerId == answer.id) ? vqas.find(x => x.vAnswerId == answer.id).alternateAnswer : null : null;
  }

  selectAnswer(event, vqa: VVerificationQuestionAnswerDto, answer: VAnswerDto) {
    if (event.detail.checked) {
      vqa.vAnswerId = answer.id;
    } else {
      vqa.vAnswerId = null;
    }
  }

  selectAnswerMultiple(event, questionId: number, vqwa: VVerificationQuestionWithAnswersDto, answer: VAnswerDto) {
    if (event.detail.checked) {
      const newVqa = new VVerificationQuestionAnswerDto();
      newVqa.vQuestionId = questionId;
      newVqa.vAnswerId = answer.id;
      vqwa.vVQAMultipleDto.push(newVqa);
      if (this.isAnswerWithFreeText(answer)) { // idk it wouldn't do the focusout for some reason
        this.saveAnswer(null, vqwa, answer);
      }
    } else if (vqwa) {
      const vqa = vqwa.vVQAMultipleDto.find(x => x.vAnswerId == answer.id);
      if (vqa && vqa.id > 0) {
        this.verificationQuestionAnswerService.delete(vqa.id).subscribe(() => {});
      }
      const index = vqwa.vVQAMultipleDto.findIndex(x => x.vAnswerId == answer.id);
      if (index != -1) {
        vqwa.vVQAMultipleDto.splice(index, 1);
      }
    }
  }

  setInProgress() {
    
    this.verification.farmName = this.formGroup.controls.farmName.value;
    this.verification.countyId = this.formGroup.controls.countyId.value;
    this.verification.latitude = this.formGroup.controls.latitude.value;
    this.verification.longitude = this.formGroup.controls.longitude.value;
    this.verification.statusId = VVerificationStatusEnum._1;
    this.verificationService.save(this.verification).subscribe();

  }

  async save(status: VVerificationStatusEnum) {

    const isCompleting = status == VVerificationStatusEnum._2;

    const errors = [];
    if (!this.formGroup.controls.farmName.value) {
      errors.push('&#8226; Farm name is required');
    }
    if (
      !this.formGroup.controls.latitude.value ||
      !this.formGroup.controls.longitude.value
    ) {
      errors.push('&#8226; Location is required');
    }
    if (errors.length > 0) {
      this.saveError(errors.join('<br>'));
      return;
    }

    const loadingMsg = isCompleting ? 'Completing Assessment...' : 'Saving Assessment...';
    const loading = await this.loadingController.create({
      message: loadingMsg,
    });
    await loading.present();
    
    this.verification.statusId = status;
    this.verification.farmName = this.formGroup.controls.farmName.value;
    this.verification.countyId = this.formGroup.controls.countyId.value;
    this.verification.latitude = this.formGroup.controls.latitude.value;
    this.verification.longitude = this.formGroup.controls.longitude.value;
    this.verificationService.save(this.verification).subscribe(
      async verification => {
        
        await loading.dismiss();

        const alert = await this.alertController.create({
          header: 'Success',
          message: 'Assessment has been saved',
          buttons: ['OK']
        });
        if (!isCompleting) await alert.present();

        if (isCompleting) this.router.navigate(['/main/verifications']);

      },
      async error => {
        this.saveError(error).then();
        await loading.dismiss();
      }
    );
  }

  async saveError(errorMessage) {
    const alert = await this.alertController.create({
      header: 'Error',
      subHeader: 'Error',
      message: errorMessage,
      buttons: ['OK']
    });

    await alert.present();
  }

  // saveVerificationQuestionAnswers() {
  //   const dtos = [];
  //   this.verificationQuestionsWithAnswers.forEach(vqa => {
  //     if (
  //       this.isSingleSelect(vqa.vQuestionDto.typeId) ||
  //       this.isFreeText(vqa.vQuestionDto.typeId)
  //     ) {
  //       vqa.vVQASingleDto.vVerificationId = this.id;
  //       dtos.push(vqa.vVQASingleDto);
  //     } else {
  //       if (vqa.vVQAMultipleDto && vqa.vVQAMultipleDto.length > 0) {
  //         vqa.vVQAMultipleDto.forEach(dto => {
  //           dto.vVerificationId = this.id;
  //           dtos.push(dto);
  //         });
  //       }
  //     }
  //   });
  //   this.verificationQuestionAnswerService.createOrUpdateAll(dtos).subscribe(async dtoResponse => {
  //     this.getVerificationQuestionsWithAnswers(false);
  //     const alert = await this.alertController.create({
  //       header: 'Success',
  //       message: 'Verification has been saved',
  //       buttons: ['OK']
  //     });
  
  //     await alert.present();
  //   });
  // }

  async saveVerificationError(errorMessage) {
    const alert = await this.alertController.create({
      header: 'Error',
      subHeader: 'Error',
      message: errorMessage,
      buttons: ['OK']
    });

    await alert.present();
  }

  cancel() {
    this.navCtrl.back();
    // this.router.navigate(['/main/home']);
  }
  
  selectAnswerMultipleFreeTextSave(event, vqa: VVerificationQuestionWithAnswersDto, answer: VAnswerDto) {
    // console.log(event);
    vqa.vVQAMultipleDto.find(x => x.vAnswerId == answer.id).alternateAnswer = event.srcElement.value;
    this.saveAnswer(null, vqa, answer);
  }

  saveAnswer(event, vqa: VVerificationQuestionWithAnswersDto, answer: VAnswerDto) {
    let dto: VVerificationQuestionAnswerDto = null;
    if (
      this.isFreeText(vqa.vQuestionDto.typeId) ||
      this.isSingleSelect(vqa.vQuestionDto.typeId) ||
      (this.isSingleSelect(vqa.vQuestionDto.typeId) && this.isAnswerWithFreeText(answer))
    ) { // grab single answer
      dto = vqa.vVQASingleDto;
    } else { // grab multi answer by answer id
      dto = vqa.vVQAMultipleDto.find(x => x.vAnswerId == answer.id);
    }
    dto.vVerificationId = this.id;
    this.verificationQuestionAnswerService.save(dto).subscribe(savedDto => {
      if (
        this.isFreeText(vqa.vQuestionDto.typeId) ||
        this.isSingleSelect(vqa.vQuestionDto.typeId) ||
        (this.isSingleSelect(vqa.vQuestionDto.typeId) && this.isAnswerWithFreeText(answer))
      ) { // grab single answer
        vqa.vVQASingleDto.id = savedDto.id;
      } else { // grab multi answer by answer id
        vqa.vVQAMultipleDto.find(x => x.vAnswerId == answer.id).id = savedDto.id;
      }
    });
    if (
      this.verification.statusId == VVerificationStatusEnum._0 // ||
      // (
      //   (this.verification.farmName == null || this.verification.farmName == '') &&
      //   (this.formGroup.controls.farmName.value != null && this.formGroup.controls.farmName.value != '')
      // ) ||
      // (
      //   (this.verification.latitude == null || this.verification.latitude == 0) &&
      //   (this.formGroup.controls.latitude.value != null && this.formGroup.controls.latitude.value != 0)
      // ) ||
      // (
      //   (this.verification.longitude == null || this.verification.longitude == 0) &&
      //   (this.formGroup.controls.longitude.value != null && this.formGroup.controls.longitude.value != 0)
      // )
    ) {
      this.setInProgress();
    }
  }

  print() {
    window.open(`${this.baseUrl}/print/assessment/${this.id}?${AppConsts.authorization.encryptedAuthTokenName}=${encodeURIComponent(this.encToken)}`, '_blank');
  }

  async addEditNote(vVQN: VVerificationQuestionNoteDto) {
    // this.verificationQuestionNoteService.getVerificationQuestionNoteAuditHistory(verificationQuestionNoteId).subscribe(async auditLogs => {
      const modal = await this.modalController.create({
        component: VerificationQuestionNotePage,
        componentProps: {
          'vVQN': vVQN,
        },
        backdropDismiss: false
      });
      modal.onDidDismiss().then((param) => {
        if (param && param.data && param.data['doSave'] && param.data['doSave'] == true) {
          vVQN.vVerificationId = this.id;
          this.verificationQuestionNoteService.save(vVQN).subscribe(response => {
            vVQN.id = response.id;
            vVQN.lastModifiedUserName = response.lastModifiedUserName;
            vVQN.lastModifiedTime = response.lastModifiedTime;
          });
        }
      });
      modal.present();
    // });
  }

  getMultiQuestionLetter(sort: number) {
    switch (sort) {
      case 1: return 'a';
      case 2: return 'b';
      case 3: return 'c';
      case 4: return 'd';
      case 5: return 'e';
      case 6: return 'f';
      case 7: return 'g';
    }
  }

  numberOfQuestionsAnswered() {
    var num = 0;
    if (this.verificationQuestionsWithAnswersGroups) {
      this.verificationQuestionsWithAnswersGroups.forEach(vQWAG => {
        var numAnswered = 0;
        vQWAG.vVQWADtos.forEach(vVQWADto => {
          vVQWADto.vAnswerDtos.forEach(vAnswerDto => {
            if (vVQWADto.vQuestionDto.typeId != VerificationQuestionTypeEnum.FreeText) {
              if (this.isSingleSelect(vVQWADto.vQuestionDto.typeId)) {
                if (this.isAnswerSelected(vVQWADto.vVQASingleDto, vAnswerDto)) {
                  if (this.isAnswerWithFreeText(vAnswerDto)) {
                    if (vVQWADto.vVQASingleDto.alternateAnswer) {
                      numAnswered++;
                    }
                  } else {
                    numAnswered++;
                  }
                }
              } else if (this.isMultiSelect(vVQWADto.vQuestionDto.typeId)) {
                if (this.isAnswerSelectedMultiple(vVQWADto.vVQAMultipleDto, vAnswerDto)) {
                  numAnswered++;
                }
              }
            } else {
              if (vVQWADto.vVQASingleDto.alternateAnswer) {
                numAnswered++;
              }
            }
          });
        });
        // if (numAnswered == vQWAG.vVQWADtos.length) num++;
        if (numAnswered >= vQWAG.vVQWADtos.length) num++;
      });
    }
    return num;
  }

  numberOfQuestions() {
    return this.verificationQuestionsWithAnswersGroups ? this.verificationQuestionsWithAnswersGroups.length : 0;
  }

  canComplete() {
    return this.verificationQuestionsWithAnswersGroups ? (this.numberOfQuestionsAnswered() / this.numberOfQuestions()) == 1 : false;
  }

}
